import { formatCurrency, formatNumber } from '@angular/common';
import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

let nextId = 0;

@Component({
  standalone: true,
  selector: 'ct-lib-salary-input',
  templateUrl: './salary-input.component.html',
  styleUrls: ['./salary-input.component.scss'],
  imports: [ReactiveFormsModule],
})
export class SalaryInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control: UntypedFormControl = new UntypedFormControl(null);
  @Input() placeholder = '';
  @Input() interval = '';
  /** ISO 4217 code, e.g. EUR */
  @Input() currencyCode = 'EUR';
  /** When false, preview shows only the number (currency shown elsewhere). */
  @Input() displayCurrencySymbol = true;

  protected readonly inputId = `salary-input-${++nextId}`;
  protected readonly previewId = `salary-preview-${nextId}`;
  protected readonly isEditing = signal(false);
  protected readonly formattedPreviewText = signal('');

  private readonly injector = inject(Injector);
  private readonly numberInput = viewChild<ElementRef<HTMLInputElement>>(
    'salaryNumberInput'
  );
  private valueSub?: Subscription;

  setValue(salary: number | null): void {
    if (salary == null) {
      this.control.setValue(null);
      this.control.markAsPristine();
    } else if (salary) {
      this.control.setValue(roundCents(salary));
    }
    this.control.updateValueAndValidity();
    this.refreshPreview();
  }

  /** Parse current input into a number on the control (e.g. before save). */
  commitValue(): void {
    this.coerceToNumber();
  }

  ngOnInit(): void {
    this.coerceToNumber();
    this.refreshPreview();
    this.valueSub = this.control.valueChanges.subscribe(() => {
      if (!this.isEditing()) {
        this.refreshPreview();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !Object.values(changes).every((c) => c.firstChange) &&
      (changes['currencyCode'] || changes['displayCurrencySymbol']) &&
      !this.isEditing()
    ) {
      this.refreshPreview();
    }
  }

  ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
  }

  onPreviewFocus(): void {
    // Number → comma string so the edit field never shows a JS period ("16.5")
    const value = this.control.value;
    if (typeof value === 'number' && Number.isFinite(value)) {
      this.control.setValue(toEditString(value), { emitEvent: false });
    }
    this.isEditing.set(true);
    afterNextRender(
      () => {
        const el = this.numberInput()?.nativeElement;
        el?.focus();
        el?.select();
      },
      { injector: this.injector }
    );
  }

  onNumberBlur(): void {
    this.coerceToNumber();
    this.isEditing.set(false);
    this.refreshPreview();
  }

  /** Digits + one comma (decimal). Block period and other chars. */
  onKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }

    const el = event.target as HTMLInputElement;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const next = el.value.slice(0, start) + event.key + el.value.slice(end);

    // Period is never allowed (would look like a decimal / thousand sep)
    if (event.key === '.' || event.code === 'Period') {
      event.preventDefault();
      return;
    }

    if (
      event.key === ',' ||
      event.key === 'Decimal' ||
      event.code === 'NumpadDecimal'
    ) {
      event.preventDefault();
      if (!el.value.includes(',')) {
        this.insertAtCaret(el, ',');
      }
      return;
    }

    if (event.key >= '0' && event.key <= '9' && event.key.length === 1) {
      const commaAt = next.indexOf(',');
      if (commaAt !== -1 && next.length - commaAt - 1 > 2) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const el = event.target as HTMLInputElement;
    const pasted = event.clipboardData?.getData('text/plain') ?? '';
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const merged = sanitizeSalaryText(
      el.value.slice(0, start) + pasted + el.value.slice(end)
    );
    this.control.setValue(merged === '' ? null : merged);
    this.control.updateValueAndValidity();
    const caret = sanitizeSalaryText(el.value.slice(0, start) + pasted).length;
    afterNextRender(
      () => el.setSelectionRange(caret, caret),
      { injector: this.injector }
    );
  }

  private insertAtCaret(el: HTMLInputElement, char: string): void {
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const next = el.value.slice(0, start) + char + el.value.slice(end);
    this.control.setValue(next);
    this.control.updateValueAndValidity();
    afterNextRender(
      () => el.setSelectionRange(start + 1, start + 1),
      { injector: this.injector }
    );
  }

  private coerceToNumber(): void {
    const value = this.control.value;
    if (value == null || value === '') {
      this.control.setValue(null, { emitEvent: false });
      this.control.updateValueAndValidity();
      return;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      const rounded = roundCents(value);
      if (rounded !== value) {
        this.control.setValue(rounded, { emitEvent: false });
      }
      this.control.updateValueAndValidity();
      return;
    }
    const parsed = parseSalaryText(String(value));
    this.control.setValue(parsed, { emitEvent: false });
    this.control.updateValueAndValidity();
  }

  private refreshPreview(): void {
    const value = this.control.value;
    const amount =
      typeof value === 'number' && Number.isFinite(value)
        ? roundCents(value)
        : parseSalaryText(value == null ? '' : String(value));
    if (amount == null) {
      this.formattedPreviewText.set('');
      return;
    }
    const formatted = this.displayCurrencySymbol
      ? formatCurrency(amount, 'de-DE', this.currencyCode, 'symbol', '1.2-2')
      : formatNumber(amount, 'de-DE', '1.2-2');
    const suffix = this.interval?.trim() ? ` ${this.interval.trim()}` : '';
    this.formattedPreviewText.set(`${formatted}${suffix}`);
  }
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Edit representation: always comma + 2 fraction digits (e.g. 16,50). */
function toEditString(value: number): string {
  return roundCents(value).toFixed(2).replace('.', ',');
}

/** Digits + optional single comma, max 2 fraction digits. Strips periods. */
function sanitizeSalaryText(raw: string): string {
  let comma = false;
  let fraction = 0;
  let out = '';
  for (const char of raw.replace(/\./g, '')) {
    if (char >= '0' && char <= '9') {
      if (comma) {
        if (fraction >= 2) continue;
        fraction += 1;
      }
      out += char;
    } else if (char === ',' && !comma) {
      comma = true;
      out += ',';
    }
  }
  return out;
}

function parseSalaryText(raw: string): number | null {
  const text = sanitizeSalaryText(raw.trim());
  if (!text || text === ',') return null;
  const normalized = text.endsWith(',')
    ? text.slice(0, -1)
    : text.replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? roundCents(n) : null;
}
